import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getStationaryDieselData, getStationaryDieselDataTotals, postStationaryDieselData } from '../../../../../actions/carbonfootprint-rework/01-direct-ghg-emissions-and-removals/01-diesel-actions';
import { dieselAndPetrolColumns, dieselAndPetrolGrandTotalFields, dieselAndPetrolTotalColumns, dryIceColumns, dryIceGrandTotalFields, dryIceTotalColumns, lpgColumns, lpgGrandTotalFields, lpgTotalColumns, refrigerantGasColumns, refrigerantGasGrandTotalFields, refrigerantGasTotalColumns } from '../constants/category-one-columns';
import { getStationaryOrMobilePetrolData, getStationaryOrMobilePetrolDataTotals, postStationaryOrMobilePetrolData } from '../../../../../actions/carbonfootprint-rework/01-direct-ghg-emissions-and-removals/02-petrol-actions';
import { getLpgData, getLpgDataTotals, postLpgData } from '../../../../../actions/carbonfootprint-rework/01-direct-ghg-emissions-and-removals/03-lpg-actions';
import { getRefrigerantGasData, getRefrigerantGasDataTotals, postRefrigerantGasData } from '../../../../../actions/carbonfootprint-rework/01-direct-ghg-emissions-and-removals/04-refrigerant-gas-actions';
import { getDryIceData, getDryIceDataTotals, postDryIceData } from '../../../../../actions/carbonfootprint-rework/01-direct-ghg-emissions-and-removals/05-dry-ice-actions';
import { getAssets } from '../../../../../actions/carbonfootprint-rework/01-direct-ghg-emissions-and-removals/01-add-asset';
import useTsPaginator from 'ts-paginator';
import { MONTHMAPPER } from '../constants/months';
import { uploadCsvDocument } from '../../../../../actions/carbonfootprint-rework/00-base/00-base-actions';

export function useCategoryOneDataStore(selectedCategory, year, company, group, facility) {
    const dispatch = useDispatch();
    const [month, setMonth] = useState(0);

    // --- declare useSelector variables ---
    const dieselData = useSelector((state) => state.directGhgEmissionsAndRemovalsForDiesel.dieselData?.data)
    const dieselTableRecords = useSelector((state) => state.directGhgEmissionsAndRemovalsForDiesel.dieselData?.pagination?.total_records)
    const dieselDataTotals = useSelector((state) => state.directGhgEmissionsAndRemovalsForDiesel.dieselDataTotals?.data)
    const petrolData = useSelector((state) => state.directGhgEmissionsAndRemovalsForPetrol.petrolData?.data)
    const petrolTableRecords = useSelector((state) => state.directGhgEmissionsAndRemovalsForPetrol.petrolData?.pagination?.total_records)
    const petrolDataTotals = useSelector((state) => state.directGhgEmissionsAndRemovalsForPetrol.petrolDataTotals?.data)
    const lpgData = useSelector((state) => state.directGhgEmissionsAndRemovalsForLpg.lpgData?.data)
    const lpgTableRecords = useSelector((state) => state.directGhgEmissionsAndRemovalsForLpg.lpgData?.pagination?.total_records)
    const lpgDataTotals = useSelector((state) => state.directGhgEmissionsAndRemovalsForLpg.lpgDataTotals?.data)
    const refrigerantGasData = useSelector((state) => state.directGhgEmissionsAndRemovalsForRefrigerantGas.refrigerantGasData?.data)
    const refrigerantGasTableRecords = useSelector((state) => state.directGhgEmissionsAndRemovalsForRefrigerantGas.refrigerantGasData?.pagination?.total_records)
    const refrigerantGasDataTotals = useSelector((state) => state.directGhgEmissionsAndRemovalsForRefrigerantGas.refrigerantGasDataTotals?.data)
    const dryIceData = useSelector((state) => state.directGhgEmissionsAndRemovalsForDryIce.dryIceData?.data)
    const dryIceTableRecords = useSelector((state) => state.directGhgEmissionsAndRemovalsForDryIce.dryIceData?.pagination?.total_records)
    const dryIceDataTotals = useSelector((state) => state.directGhgEmissionsAndRemovalsForDryIce.dryIceDataTotals?.data)

    // --- unique variable used for Petrol and Diesel
    const assetData = useSelector((state) => state.directGhgEmissionsAndRemovalsForAssets.assetData?.data)

    // --- variable manipulation, to make hook call cleaner ---

    const selectedCategoryDataMap = {
        'Diesel - Stationary': {
            data: dieselData,
            totals: dieselDataTotals,
            getDataAction: getStationaryDieselData,
            getTotalsAction: getStationaryDieselDataTotals,
            postDataAction: postStationaryDieselData,
            categoryKey: 'stationary',
            // --- backend quirk, fuelType is used to differentiate between diesel and petrol
            fuelType: 'diesel',
            columns: dieselAndPetrolColumns(assetData),
            totalColumns: dieselAndPetrolTotalColumns,
            grandTotalFields: dieselAndPetrolGrandTotalFields,
            tableRecordCount: dieselTableRecords,
        },
        'Diesel - Mobile': {
            data: dieselData,
            totals: dieselDataTotals,
            getDataAction: getStationaryDieselData,
            getTotalsAction: getStationaryDieselDataTotals,
            postDataAction: postStationaryDieselData,
            categoryKey: 'mobile',
            // --- backend quirk, fuelType is used to differentiate between diesel and petrol
            fuelType: 'diesel',
            columns: dieselAndPetrolColumns(assetData),
            totalColumns: dieselAndPetrolTotalColumns,
            grandTotalFields: dieselAndPetrolGrandTotalFields,
            tableRecordCount: dieselTableRecords,
        },
        'Petrol - Mobile': {
            data: petrolData,
            totals: petrolDataTotals,
            getDataAction: getStationaryOrMobilePetrolData,
            getTotalsAction: getStationaryOrMobilePetrolDataTotals,
            postDataAction: postStationaryOrMobilePetrolData,
            categoryKey: 'mobile',
            // --- backend quirk, fuelType is used to differentiate between diesel and petrol
            fuelType: 'petrol',
            columns: dieselAndPetrolColumns(assetData),
            totalColumns: dieselAndPetrolTotalColumns,
            grandTotalFields: dieselAndPetrolGrandTotalFields,
            tableRecordCount: petrolTableRecords,
        },
        'LPG': {
            data: lpgData,
            totals: lpgDataTotals,
            getDataAction: getLpgData,
            getTotalsAction: getLpgDataTotals,
            postDataAction: postLpgData,
            categoryKey: 'lpg',
            columns: lpgColumns,
            totalColumns: lpgTotalColumns,
            grandTotalFields: lpgGrandTotalFields,
            tableRecordCount: lpgTableRecords,
        },
        'Refrigerant Gas': {
            data: refrigerantGasData,
            totals: refrigerantGasDataTotals,
            getDataAction: getRefrigerantGasData,
            getTotalsAction: getRefrigerantGasDataTotals,
            postDataAction: postRefrigerantGasData,
            categoryKey: 'refrigerant-gas',
            columns: refrigerantGasColumns,
            totalColumns: refrigerantGasTotalColumns,
            grandTotalFields: refrigerantGasGrandTotalFields,
            tableRecordCount: refrigerantGasTableRecords,
        },
        'Dry Ice': {
            data: dryIceData,
            totals: dryIceDataTotals,
            getDataAction: getDryIceData,
            getTotalsAction: getDryIceDataTotals,
            postDataAction: postDryIceData,
            categoryKey: 'dry-ice',
            columns: dryIceColumns,
            totalColumns: dryIceTotalColumns,
            grandTotalFields: dryIceGrandTotalFields,
            tableRecordCount: dryIceTableRecords,
        },
    }

    const {
        data,
        totals,
        getDataAction,
        getTotalsAction,
        postDataAction,
        categoryKey,
        fuelType,
        columns,
        totalColumns,
        grandTotalFields,
        tableRecordCount,
    } = selectedCategoryDataMap[selectedCategory] ||
        {
            data: null,
            totals: null,
            getDataAction: null,
            getTotalsAction: null,
            postDataAction: null,
            categoryKey: null,
            fuelType: null,
            columns: [],
            totalColumns: [],
            grandTotalFields: [],
            tableRecordCount: 0
        };

    const {
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        // _determinePaginationPages,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = useTsPaginator(+tableRecordCount || 0, 0);

    useEffect(() => {
        // if (currentProjectId !== projectId) {
        _handleChangePage(0); // Reset to the first page
        _handleChangeRowsPerPage(10)
        _handleChangeTotalRecordCount(+tableRecordCount); // Reset to the first page
        // }
    }, [tableRecordCount]);


    useEffect(() => {
        const monthForBackend = MONTHMAPPER[month]
        if (selectedCategory === 'Diesel - Stationary') {
            dispatch(getStationaryDieselData('stationary', year, facility, rowsPerPage, currentPage, monthForBackend))
            dispatch(getAssets(group))
        }
        if (selectedCategory === 'Diesel - Mobile') {
            dispatch(getStationaryDieselData('mobile', year, facility, rowsPerPage, currentPage, monthForBackend))
            dispatch(getAssets(group))
        }
        if (selectedCategory === 'Petrol - Mobile') {
            dispatch(getStationaryOrMobilePetrolData('mobile', year, facility, rowsPerPage, currentPage, monthForBackend))
            dispatch(getAssets(group))
        }
        if (selectedCategory === 'LPG') {
            dispatch(getLpgData(year, facility, rowsPerPage, currentPage, monthForBackend))
        }
        if (selectedCategory === 'Refrigerant Gas') {
            dispatch(getRefrigerantGasData(year, facility, rowsPerPage, currentPage, monthForBackend))
        }
        if (selectedCategory === 'Dry Ice') {
            dispatch(getDryIceData(year, facility, rowsPerPage, currentPage, monthForBackend))
        }

    }, [dispatch, selectedCategory, year, facility, group, company, rowsPerPage, currentPage]
    )

    return {
        month,
        setMonth,
        data,
        assetData,
        totals,
        getDataAction,
        getTotalsAction,
        postDataAction,
        uploadCsvDocument,
        categoryKey,
        fuelType,
        columns,
        totalColumns,
        grandTotalFields,
        year,
        facility,
        group,
        company,
        // pagination
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        // _determinePaginationPages,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    }
}